import { memo, useState } from "react";

import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Anchor, Stack, Text, TextProps } from "@mantine/core";
import { IconArrowRightBar } from "@tabler/icons";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";

type TokenDescriptionProps = {
  description: string;
  hasExpand?: boolean;
  numChars?: number;
} & TextProps;

const TokenDescription = ({
  description,
  hasExpand,
  numChars,
  ...rest
}: TokenDescriptionProps) => {
  const enableExpanded = description.length > (numChars ?? 0);
  const [expanded, setExpanded] = useState(!hasExpand);
  const descriptionClean = description?.replace(/\\n/g, "\n") ?? "";
  const finalText = expanded
    ? descriptionClean
    : descriptionClean.slice(0, numChars) + "...";

  return (
    <Stack spacing={0}>
      <Text
        size="sm"
        {...rest}
        onClick={() =>
          hasExpand && enableExpanded ? setExpanded((exp) => !exp) : undefined
        }
      >
        <ReactMarkdown
          remarkPlugins={[gfm]}
          components={{
            a: (props) => (
              <Anchor
                {...props}
                sx={{
                  fontSize: 14,
                  textDecoration: "underline",
                  wordBreak: "break-word",
                }}
              />
            ),
            img: (props) => {
              if (props.src?.startsWith("https://www.youtube.com/embed/")) {
                return (
                  <iframe
                    width="100%"
                    style={{ aspectRatio: "16/9" }}
                    src={props.src}
                    title={props.alt ?? "YouTube embed"}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                );
              }
              return <img {...props} style={{ maxWidth: "100%" }} />;
            },
          }}
        >
          {finalText}
        </ReactMarkdown>
      </Text>
      {!expanded && enableExpanded && (
        <Text
          onClick={() => setExpanded(true)}
          fw={WEIGHT_BOLD}
          size="sm"
          sx={{
            alignItems: "center",
            display: "flex",
            cursor: "pointer",
            width: "fit-content",
          }}
        >
          Read more&nbsp;
          <IconArrowRightBar width={16} />
        </Text>
      )}
    </Stack>
  );
};

export default memo(TokenDescription);
